import React, { useEffect, useState, useRef, Fragment } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users, UserPlus, Upload, Link2, Mail, Shield, Search, Filter, MoreVertical, Edit, Trash2, CheckCircle, XCircle, Copy, Download, FileSpreadsheet, AlertCircle, Clock, TrendingUp, Activity, BookOpen, Map, X, KeyRound } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function UserManagementPage() {
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [userType, setUserType] = useState<'learners' | 'admins'>('learners');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [generatedLink, setGeneratedLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const licenseDropdownRef = useRef<HTMLDivElement>(null);
  const linkLicenseDropdownRef = useRef<HTMLDivElement>(null);
  const actionMenuRefs = useRef<{
    [key: number]: HTMLDivElement | null;
  }>({});
  // Form states
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('learner');
  const [selectedLicenses, setSelectedLicenses] = useState<string[]>([]);
  const [isLicenseDropdownOpen, setIsLicenseDropdownOpen] = useState(false);
  const [licenseSearch, setLicenseSearch] = useState('');
  const [addModalStep, setAddModalStep] = useState<'form' | 'confirm'>('form');
  const [acknowledgeChecked, setAcknowledgeChecked] = useState(false);
  const [selectedModalPathways, setSelectedModalPathways] = useState<string[]>([]);
  const [linkLicenses, setLinkLicenses] = useState<string[]>([]);
  const [isLinkLicenseDropdownOpen, setIsLinkLicenseDropdownOpen] = useState(false);
  const [linkLicenseSearch, setLinkLicenseSearch] = useState('');
  const [linkPathways, setLinkPathways] = useState<string[]>([]);
  // New State for Bulk Actions
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [bulkSelectedSkills, setBulkSelectedSkills] = useState<string[]>([]);
  const [bulkSelectedPathways, setBulkSelectedPathways] = useState<string[]>([]);
  const [isBulkSkillDropdownOpen, setIsBulkSkillDropdownOpen] = useState(false);
  const [isBulkPathwayDropdownOpen, setIsBulkPathwayDropdownOpen] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [openActionMenuId, setOpenActionMenuId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [nameSortDir, setNameSortDir] = useState<'asc' | 'desc'>('asc');
  const bulkSkillDropdownRef = useRef<HTMLDivElement>(null);
  const bulkPathwayDropdownRef = useRef<HTMLDivElement>(null);
  const availableSkills = ['AI Functionality Beyond the Hype', 'Active and Empathetic Listening', 'DEI: Employee Relations and Conflict Resolution', 'Developing EQ and Empathy', 'EQ for the Workplace', 'Effective Communication'];
  const availablePathways = ['Leadership Essentials', 'Technical Foundations', 'Communication Mastery'];
  const pathwayData = [{
    name: 'Leadership Essentials',
    skillCount: 3
  }, {
    name: 'DEI Fundamentals',
    skillCount: 2
  }, {
    name: 'Technical Foundations',
    skillCount: 4
  }, {
    name: 'Communication Mastery',
    skillCount: 3
  }];
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);
  // Close license dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (licenseDropdownRef.current && !licenseDropdownRef.current.contains(event.target as Node)) {
        setIsLicenseDropdownOpen(false);
      }
      if (linkLicenseDropdownRef.current && !linkLicenseDropdownRef.current.contains(event.target as Node)) {
        setIsLinkLicenseDropdownOpen(false);
      }
      if (bulkSkillDropdownRef.current && !bulkSkillDropdownRef.current.contains(event.target as Node)) {
        setIsBulkSkillDropdownOpen(false);
      }
      if (bulkPathwayDropdownRef.current && !bulkPathwayDropdownRef.current.contains(event.target as Node)) {
        setIsBulkPathwayDropdownOpen(false);
      }
      // Close action menu if clicking outside
      if (openActionMenuId !== null) {
        const menuRef = actionMenuRefs.current[openActionMenuId];
        if (menuRef && !menuRef.contains(event.target as Node)) {
          setOpenActionMenuId(null);
        }
      }
    };
    if (isLicenseDropdownOpen || isLinkLicenseDropdownOpen || isBulkSkillDropdownOpen || isBulkPathwayDropdownOpen || openActionMenuId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLicenseDropdownOpen, isLinkLicenseDropdownOpen, isBulkSkillDropdownOpen, isBulkPathwayDropdownOpen, openActionMenuId]);
  // Mock data
  const quickStats = [{
    label: 'Total Learners',
    value: '248',
    change: '+12',
    trend: 'up',
    icon: Users
  }, {
    label: 'Active Admins',
    value: '8',
    change: '+2',
    trend: 'up',
    icon: Shield
  }, {
    label: 'Pending Invites',
    value: '18',
    change: '-5',
    trend: 'down',
    icon: Mail
  }, {
    label: 'Registered This Month',
    value: '32',
    change: '+8',
    trend: 'up',
    icon: TrendingUp
  }];
  const [learners, setLearners] = useState([{
    id: 1,
    name: 'Sarah Johnson',
    email: 'sarah.j@example.com',
    status: 'active',
    skills: 4,
    lastActive: '2024-01-15',
    joinDate: '2023-09-12',
    assignedSkills: ['Active and Empathetic Listening', 'Effective Communication', 'EQ for the Workplace', 'Developing EQ and Empathy'],
    assignedPathways: ['Leadership Essentials', 'Communication Mastery']
  }, {
    id: 2,
    name: 'Michael Chen',
    email: 'mchen@example.com',
    status: 'active',
    skills: 3,
    lastActive: '2024-01-14',
    joinDate: '2023-10-05',
    assignedSkills: ['AI Functionality Beyond the Hype', 'Effective Communication', 'EQ for the Workplace'],
    assignedPathways: ['Technical Foundations']
  }, {
    id: 3,
    name: 'Emily Davis',
    email: 'emily.d@example.com',
    status: 'active',
    skills: 2,
    lastActive: '2024-01-13',
    joinDate: '2023-11-20',
    assignedSkills: ['DEI: Employee Relations and Conflict Resolution', 'Developing EQ and Empathy'],
    assignedPathways: ['Leadership Essentials']
  }, {
    id: 4,
    name: 'Rachel Green',
    email: 'rachel.g@example.com',
    status: 'inactive',
    skills: 2,
    lastActive: '2023-11-30',
    joinDate: '2023-07-20',
    assignedSkills: ['Developing EQ and Empathy', 'EQ for the Workplace'],
    assignedPathways: ['Leadership Essentials']
  }]);
  const admins = [{
    id: 1,
    name: 'Linda Taylor',
    email: 'linda.taylor@example.com',
    status: 'active',
    role: 'Super Admin',
    lastActive: '2024-01-15',
    joinDate: '2023-01-10'
  }, {
    id: 2,
    name: 'Robert Martinez',
    email: 'robert.m@example.com',
    status: 'active',
    role: 'Admin',
    lastActive: '2024-01-14',
    joinDate: '2023-03-22'
  }, {
    id: 3,
    name: 'Jennifer Lee',
    email: 'jennifer.lee@example.com',
    status: 'active',
    role: 'Admin',
    lastActive: '2024-01-13',
    joinDate: '2023-05-18'
  }];
  const filteredUsers = (userType === 'learners' ? learners : admins).filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase()) || user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = selectedFilter === 'all' || (selectedFilter === 'not_assigned' ? 'assignedSkills' in user && Array.isArray((user as any).assignedSkills) && (user as any).assignedSkills.length === 0 : user.status === selectedFilter);
    return matchesSearch && matchesFilter;
  }).sort((a, b) => {
    const nameA = a.name.toLowerCase();
    const nameB = b.name.toLowerCase();
    return nameSortDir === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
  });
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / itemsPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedUsers = filteredUsers.slice((safeCurrentPage - 1) * itemsPerPage, safeCurrentPage * itemsPerPage);
  const handleGenerateLink = () => {
    const linkType = userType === 'learners' ? 'learner' : 'admin';
    const newLink = `https://platform.example.com/register/${linkType}?token=${Math.random().toString(36).substr(2, 9)}`;
    setGeneratedLink(newLink);
  };
  const handleCopyLink = () => {
    navigator.clipboard.writeText(generatedLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };
  const handleAddUser = () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim()) return;
    // Handle user addition logic
    setShowAddModal(false);
    setFirstName('');
    setLastName('');
    setEmail('');
    setRole('learner');
    setSelectedLicenses([]);
    setSelectedModalPathways([]);
    setLicenseSearch('');
    setAddModalStep('form');
    setAcknowledgeChecked(false);
  };
  const toggleLicense = (skill: string) => {
    setSelectedLicenses(prev => prev.includes(skill) ? prev.filter(s => s !== skill) : [...prev, skill]);
  };
  const toggleModalPathway = (pathway: string) => {
    setSelectedModalPathways(prev => prev.includes(pathway) ? prev.filter(p => p !== pathway) : [...prev, pathway]);
  };
  const selectAllModalPathways = () => {
    const allPathwayNames = pathwayData.map(p => p.name);
    if (selectedModalPathways.length === allPathwayNames.length) {
      setSelectedModalPathways([]);
    } else {
      setSelectedModalPathways([...allPathwayNames]);
    }
  };
  const selectAllModalSkills = () => {
    if (selectedLicenses.length === availableSkills.length) {
      setSelectedLicenses([]);
    } else {
      setSelectedLicenses([...availableSkills]);
    }
  };
  const toggleLinkPathway = (pathway: string) => {
    setLinkPathways(prev => prev.includes(pathway) ? prev.filter(p => p !== pathway) : [...prev, pathway]);
  };
  const selectAllLinkPathways = () => {
    const allPathwayNames = pathwayData.map(p => p.name);
    if (linkPathways.length === allPathwayNames.length) {
      setLinkPathways([]);
    } else {
      setLinkPathways([...allPathwayNames]);
    }
  };
  const selectAllLinkSkills = () => {
    if (linkLicenses.length === availableSkills.length) {
      setLinkLicenses([]);
    } else {
      setLinkLicenses([...availableSkills]);
    }
  };
  const toggleLinkLicense = (skill: string) => {
    setLinkLicenses(prev => prev.includes(skill) ? prev.filter(s => s !== skill) : [...prev, skill]);
  };
  // Bulk Actions Handlers
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedUsers(filteredUsers.map(u => u.id));
    } else {
      setSelectedUsers([]);
    }
  };
  const handleSelectUser = (id: number) => {
    setSelectedUsers(prev => prev.includes(id) ? prev.filter(userId => userId !== id) : [...prev, id]);
  };
  const toggleBulkSkill = (skill: string) => {
    if (skill === 'all') {
      if (bulkSelectedSkills.length === availableSkills.length) {
        setBulkSelectedSkills([]);
      } else {
        setBulkSelectedSkills([...availableSkills]);
      }
      return;
    }
    setBulkSelectedSkills(prev => prev.includes(skill) ? prev.filter(s => s !== skill) : [...prev, skill]);
  };
  const toggleBulkPathway = (pathway: string) => {
    if (pathway === 'all') {
      if (bulkSelectedPathways.length === availablePathways.length) {
        setBulkSelectedPathways([]);
      } else {
        setBulkSelectedPathways([...availablePathways]);
      }
      return;
    }
    setBulkSelectedPathways(prev => prev.includes(pathway) ? prev.filter(p => p !== pathway) : [...prev, pathway]);
  };
  const handleBulkAssign = () => {
    // Simulate assigning skills/pathways
    const totalAssigned = bulkSelectedSkills.length + bulkSelectedPathways.length;
    if (totalAssigned === 0) return;
    setLearners(prev => prev.map(learner => {
      if (selectedUsers.includes(learner.id)) {
        return {
          ...learner,
          skills: learner.skills + totalAssigned,
          status: learner.status === 'pending' ? 'inactive' : learner.status,
          assignedSkills: [...(learner.assignedSkills || []), ...bulkSelectedSkills],
          assignedPathways: [...(learner.assignedPathways || []), ...bulkSelectedPathways]
        };
      }
      return learner;
    }));
    setToastMessage(`Successfully assigned ${totalAssigned} items to ${selectedUsers.length} learners.`);
    setShowSuccessToast(true);
    setTimeout(() => setShowSuccessToast(false), 3000);
    // Reset state
    setSelectedUsers([]);
    setBulkSelectedSkills([]);
    setBulkSelectedPathways([]);
    setIsBulkSkillDropdownOpen(false);
    setIsBulkPathwayDropdownOpen(false);
  };
  const handleSimulateBulkUpload = () => {
    setShowBulkModal(false);
    // Simulate adding new users
    const newLearners = [{
      id: 5,
      name: 'John Smith',
      email: 'john.smith@example.com',
      status: 'pending',
      skills: 0,
      lastActive: 'Never',
      joinDate: new Date().toISOString().split('T')[0],
      assignedSkills: [],
      assignedPathways: []
    }, {
      id: 6,
      name: 'Lisa Wong',
      email: 'lisa.w@example.com',
      status: 'pending',
      skills: 0,
      lastActive: 'Never',
      joinDate: new Date().toISOString().split('T')[0],
      assignedSkills: [],
      assignedPathways: []
    }, {
      id: 7,
      name: 'Alex Rivera',
      email: 'alex.r@example.com',
      status: 'pending',
      skills: 0,
      lastActive: 'Never',
      joinDate: new Date().toISOString().split('T')[0],
      assignedSkills: [],
      assignedPathways: []
    }];
    setLearners(prev => [...newLearners, ...prev]);
    // Auto-select the newly added users
    setSelectedUsers(newLearners.map(l => l.id));
    setToastMessage(`Successfully imported ${newLearners.length} learners. Please assign them skills.`);
    setShowSuccessToast(true);
    setTimeout(() => setShowSuccessToast(false), 4000);
  };
  return <div className="flex-1 bg-gray-50 min-h-screen relative pb-24" data-id="element-2217">
      {/* Success Toast */}
      <AnimatePresence data-id="element-2218">
        {showSuccessToast && <motion.div initial={{
        opacity: 0,
        y: -50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -50
      }} className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-[#007558] text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3" data-id="element-2219">
            <CheckCircle size={20} data-id="element-2220" />
            <span className="font-medium" data-id="element-2221">{toastMessage}</span>
          </motion.div>}
      </AnimatePresence>

      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-2222">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2223">
          <div className="flex items-center h-16" data-id="element-2224">
            {/* Logo */}
            <div className="text-[#DB3725] font-bold text-xl" data-id="element-2225">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-2226">
              <Link to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2227">
                <Home size={18} data-id="element-2228" />
                <span className="hidden sm:inline" data-id="element-2229">Admin Hub</span>
              </Link>
              <Link to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2230">
                <BarChart3 size={18} data-id="element-2231" />
                <span className="hidden sm:inline" data-id="element-2232">Analytics</span>
              </Link>
              <Link to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2233">
                <FileText size={18} data-id="element-2234" />
                <span className="hidden sm:inline" data-id="element-2235">Reports</span>
              </Link>
              <Link to="/assessment" className="flex items-center gap-2 text-[#30718D] border-b-2 border-[#30718D] pb-[22px] pt-[22px] transition-colors text-sm font-medium" data-id="element-2236">
                <UserCog size={18} data-id="element-2237" />
                <span className="hidden sm:inline" data-id="element-2238">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-2239">
              <Link to="/alerts" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-2240">
                <Bell size={20} data-id="element-2241" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-2242"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-2243">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-2244">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-2245">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-2246">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-2247" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-2248">
                    <div className="py-2" data-id="element-2249">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2250">
                        <Users size={18} className="text-gray-600" data-id="element-2251" />
                        <span className="font-medium text-gray-900" data-id="element-2252">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2253">
                        <Home size={18} className="text-gray-600" data-id="element-2254" />
                        <span className="font-medium text-gray-900" data-id="element-2255">
                          Learner Hub
                        </span>
                      </button>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2256">
                        <UserCog size={18} className="text-gray-600" data-id="element-2257" />
                        <span className="font-medium text-gray-900" data-id="element-2258">
                          Admin Hub
                        </span>
                      </button>
                      <Link to="/settings" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2259">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-2260">
                          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" data-id="element-2261"></path>
                          <circle cx="12" cy="12" r="3" data-id="element-2262"></circle>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-2263">
                          Settings
                        </span>
                      </Link>
                      <div className="border-t border-gray-200 my-2" data-id="element-2264"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2265">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-2266">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-2267"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-2268"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-2269"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-2270">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-id="element-2271">
        {/* Header with Toggle */}
        <div className="mb-8" data-id="element-2272">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4" data-id="element-2273">
            <div data-id="element-2274">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2" data-id="element-2275">
                User Management
              </h1>
              <p className="text-gray-600 text-sm sm:text-base" data-id="element-2276">
                Manage {userType === 'learners' ? 'learners' : 'administrators'}{' '}
                across your platform
              </p>
            </div>

            {/* User Type Toggle */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 self-start" data-id="element-2277">
              <button onClick={() => setUserType('learners')} className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${userType === 'learners' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-2278">
                <span className="flex items-center gap-2" data-id="element-2279">
                  <Users size={16} data-id="element-2280" />
                  Learners
                </span>
              </button>
              <button onClick={() => setUserType('admins')} className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${userType === 'admins' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-2281">
                <span className="flex items-center gap-2" data-id="element-2282">
                  <Shield size={16} data-id="element-2283" />
                  Admins
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Licenses Remaining Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.05
      }} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 mb-8" data-id="element-2284">
          <div className="flex items-center gap-3 mb-4" data-id="element-2285">
            <div className="w-8 h-8 rounded-lg bg-[#DB3725]/10 flex items-center justify-center flex-shrink-0" data-id="element-2286">
              <KeyRound size={16} className="text-[#DB3725]" data-id="element-2287" />
            </div>
            <div data-id="element-2288">
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-2289">
                Licenses Remaining
              </div>
              <div className="text-base font-semibold text-gray-900" data-id="element-2290">
                180{' '}
                <span className="text-sm font-medium text-gray-500" data-id="element-2291">
                  of 280
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1.5" data-id="element-2292">
            {[{
            name: 'Scrum Master Fundamentals Course',
            used: 10,
            total: 20
          }, {
            name: 'Digital Marketing Track',
            used: 3,
            total: 20
          }, {
            name: 'IT Bundle',
            used: 75,
            total: 100
          }, {
            name: 'Anatomy, Physiology and Coding I Course',
            used: 5,
            total: 20
          }, {
            name: 'AWS Solutions Architect Track',
            used: 7,
            total: 20
          }, {
            name: 'Human Resources Bundle',
            used: 80,
            total: 100
          }].map((license, idx) => {
            return <div key={idx} className="flex items-baseline gap-2 py-0.5" data-id="element-2293">
                  <span className="text-gray-300 text-xs" data-id="element-2294">•</span>
                  <span className="text-sm text-gray-600 leading-snug" data-id="element-2295">
                    <span className="font-semibold text-gray-900 tabular-nums" data-id="element-2296">
                      {license.used} of {license.total}
                    </span>{' '}
                    {license.name}
                  </span>
                </div>;
          })}
          </div>
        </motion.div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8" data-id="element-2297">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} onClick={() => setShowAddModal(true)} className="group bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all cursor-pointer" data-id="element-2298">
            <div className="flex items-center gap-4" data-id="element-2299">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#30718D] to-[#265a70] flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-2300">
                <UserPlus size={24} className="text-white" data-id="element-2301" />
              </div>
              <div className="flex-1" data-id="element-2302">
                <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-[#30718D] transition-colors" data-id="element-2303">
                  Add Individual {userType === 'learners' ? 'Learner' : 'Admin'}
                </h3>
                <p className="text-sm text-gray-600" data-id="element-2304">
                  Register a single{' '}
                  {userType === 'learners' ? 'learner' : 'administrator'}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} onClick={() => setShowBulkModal(true)} className="group bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all cursor-pointer" data-id="element-2305">
            <div className="flex items-center gap-4" data-id="element-2306">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#007558] to-[#005a44] flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-2307">
                <Upload size={24} className="text-white" data-id="element-2308" />
              </div>
              <div className="flex-1" data-id="element-2309">
                <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-[#007558] transition-colors" data-id="element-2310">
                  Bulk Upload {userType === 'learners' ? 'Learners' : 'Admins'}
                </h3>
                <p className="text-sm text-gray-600" data-id="element-2311">
                  Import multiple{' '}
                  {userType === 'learners' ? 'learners' : 'admins'} via CSV
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6
        }} onClick={() => {
          setShowLinkModal(true);
          handleGenerateLink();
        }} className="group bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all cursor-pointer" data-id="element-2312">
            <div className="flex items-center gap-4" data-id="element-2313">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-2314">
                <Link2 size={24} className="text-white" data-id="element-2315" />
              </div>
              <div className="flex-1" data-id="element-2316">
                <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-purple-600 transition-colors" data-id="element-2317">
                  Generate {userType === 'learners' ? 'Learner' : 'Admin'} Link
                </h3>
                <p className="text-sm text-gray-600" data-id="element-2318">
                  Create self-registration URL
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* User List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100" data-id="element-2319">
          <div className="p-4 sm:p-6 border-b border-gray-200" data-id="element-2320">
            <div className="flex items-center justify-between mb-4" data-id="element-2321">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900" data-id="element-2322">
                {userType === 'learners' ? 'All Learners' : 'All Administrators'}{' '}
                ({filteredUsers.length})
              </h2>
            </div>

            {/* Search and Filter - Stack on mobile */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4" data-id="element-2323">
              <div className="flex-1 relative" data-id="element-2324">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2325" />
                <input type="text" placeholder="Search users..." value={searchQuery} onChange={e => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent text-sm" data-id="element-2326" />
              </div>
              <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1 overflow-x-auto" data-id="element-2327">
                <button onClick={() => {
                setSelectedFilter('all');
                setCurrentPage(1);
              }} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${selectedFilter === 'all' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-2328">
                  All
                </button>
                <button onClick={() => {
                setSelectedFilter('active');
                setCurrentPage(1);
              }} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${selectedFilter === 'active' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-2329">
                  Active
                </button>
                <button onClick={() => {
                setSelectedFilter('inactive');
                setCurrentPage(1);
              }} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${selectedFilter === 'inactive' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-2330">
                  Inactive
                </button>
                <button onClick={() => {
                setSelectedFilter('pending');
                setCurrentPage(1);
              }} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${selectedFilter === 'pending' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-2331">
                  Pending
                </button>
                <button onClick={() => {
                setSelectedFilter('not_assigned');
                setCurrentPage(1);
              }} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${selectedFilter === 'not_assigned' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-2332">
                  Not Assigned
                </button>
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="overflow-x-auto" data-id="element-2333">
            <table className="w-full min-w-0" data-id="element-2334">
              <thead data-id="element-2335">
                <tr className="border-b border-gray-200 bg-gray-50" data-id="element-2336">
                  <th className="py-3 pl-3 pr-1 sm:px-6 w-8 sm:w-12" data-id="element-2337">
                    <input type="checkbox" checked={filteredUsers.length > 0 && selectedUsers.length === filteredUsers.length} onChange={handleSelectAll} className="w-4 h-4 rounded border-gray-300 text-[#30718D] focus:ring-[#30718D] transition-colors cursor-pointer" data-id="element-2338" />
                  </th>
                  <th className="text-left py-3 px-3 sm:px-6 font-semibold text-sm text-gray-700" data-id="element-2339">
                    <button onClick={() => setNameSortDir(nameSortDir === 'asc' ? 'desc' : 'asc')} className="flex items-center gap-1 hover:text-gray-900 transition-colors" data-id="element-2340">
                      Name
                      <span className="text-xs text-gray-400" data-id="element-2341">
                        {nameSortDir === 'asc' ? '↑' : '↓'}
                      </span>
                    </button>
                  </th>
                  <th className="text-left py-3 px-3 sm:px-6 font-semibold text-sm text-gray-700 hidden sm:table-cell" data-id="element-2342">
                    Email
                  </th>
                  <th className="text-left py-3 px-3 sm:px-6 font-semibold text-sm text-gray-700" data-id="element-2343">
                    Status
                  </th>
                  {userType === 'learners' ? <th className="text-left py-3 px-3 sm:px-6 font-semibold text-sm text-gray-700 hidden sm:table-cell" data-id="element-2344">
                      Skills Completed
                    </th> : <th className="text-left py-3 px-3 sm:px-6 font-semibold text-sm text-gray-700 hidden sm:table-cell" data-id="element-2345">
                      Role
                    </th>}
                  <th className="text-left py-3 px-3 sm:px-6 font-semibold text-sm text-gray-700 hidden sm:table-cell" data-id="element-2346">
                    Last Active
                  </th>
                  <th className="text-left py-3 px-2 sm:px-6 font-semibold text-sm text-gray-700 w-12 sm:w-auto" data-id="element-2347">
                    <span className="hidden sm:inline" data-id="element-2348">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-2349">
                <AnimatePresence mode="wait" data-id="element-2350">
                  {paginatedUsers.map((user, index) => <Fragment key={`${userType}-${user.id}`} data-id="element-2351">
                      <motion.tr initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -10
                  }} transition={{
                    delay: index * 0.05
                  }} onClick={e => {
                    const target = e.target as HTMLElement;
                    if (!target.closest('input[type="checkbox"]') && !target.closest('button')) {
                      setExpandedUserId(expandedUserId === user.id ? null : user.id);
                    }
                  }} className={`border-b border-gray-100 transition-colors cursor-pointer ${expandedUserId === user.id ? 'bg-gray-50' : 'hover:bg-gray-50'}`} data-id="element-2352">
                        <td className="py-3 sm:py-4 pl-3 pr-1 sm:px-6" data-id="element-2353">
                          <input type="checkbox" checked={selectedUsers.includes(user.id)} onChange={() => handleSelectUser(user.id)} className="w-4 h-4 rounded border-gray-300 text-[#30718D] focus:ring-[#30718D] transition-colors cursor-pointer" data-id="element-2354" />
                        </td>
                        <td className="py-3 sm:py-4 px-3 sm:px-6" data-id="element-2355">
                          <div className="flex items-center gap-2 sm:gap-3" data-id="element-2356">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#30718D] flex items-center justify-center text-white text-xs sm:text-sm font-bold flex-shrink-0" data-id="element-2357">
                              {user.name.split(' ').map(n => n[0]).join('')}
                            </div>
                            <div className="min-w-0" data-id="element-2358">
                              <div className="font-medium text-gray-900 text-sm truncate" data-id="element-2359">
                                {user.name}
                              </div>
                              <div className="text-xs text-gray-500 sm:hidden truncate" data-id="element-2360">
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 sm:py-4 px-3 sm:px-6 text-sm text-gray-600 hidden sm:table-cell" data-id="element-2361">
                          {user.email}
                        </td>
                        <td className="py-3 sm:py-4 px-3 sm:px-6" data-id="element-2362">
                          <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${user.status === 'active' ? 'bg-[#007558]/10 text-[#007558]' : user.status === 'inactive' ? 'bg-gray-100 text-gray-600' : 'bg-yellow-100 text-yellow-700'}`} data-id="element-2363">
                            {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                          </span>
                        </td>
                        <td className="py-3 sm:py-4 px-3 sm:px-6 text-sm text-gray-600 hidden sm:table-cell" data-id="element-2364">
                          {userType === 'learners' ? user.skills > 0 ? `${user.skills} skills` : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700" data-id="element-2365">
                                <AlertCircle size={12} data-id="element-2366" />
                                Not assigned
                              </span> : user.role}
                        </td>
                        <td className="py-3 sm:py-4 px-3 sm:px-6 text-sm text-gray-600 hidden sm:table-cell" data-id="element-2367">
                          {user.lastActive}
                        </td>
                        <td className="py-3 sm:py-4 px-1 sm:px-6" data-id="element-2368">
                          <div className="flex items-center justify-center" data-id="element-2369">
                            <div className="relative" ref={el => actionMenuRefs.current[user.id] = el} data-id="element-2370">
                              <button onClick={() => setOpenActionMenuId(openActionMenuId === user.id ? null : user.id)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-2371">
                                <MoreVertical size={16} data-id="element-2372" />
                              </button>

                              <AnimatePresence data-id="element-2373">
                                {openActionMenuId === user.id && <motion.div initial={{
                              opacity: 0,
                              scale: 0.95,
                              y: -10
                            }} animate={{
                              opacity: 1,
                              scale: 1,
                              y: 0
                            }} exit={{
                              opacity: 0,
                              scale: 0.95,
                              y: -10
                            }} transition={{
                              duration: 0.15
                            }} className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden" data-id="element-2374">
                                    <button onClick={() => navigate('/analytics')} className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm border-b border-gray-100" data-id="element-2375">
                                      <Users size={16} className="text-gray-600" data-id="element-2376" />
                                      <span className="font-medium text-gray-900" data-id="element-2377">
                                        View Learner
                                      </span>
                                    </button>
                                    <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2378">
                                      <Mail size={16} className="text-gray-600" data-id="element-2379" />
                                      <span className="font-medium text-gray-900" data-id="element-2380">
                                        Email
                                      </span>
                                    </button>
                                  </motion.div>}
                              </AnimatePresence>
                            </div>
                          </div>
                        </td>
                      </motion.tr>

                      {/* Expanded Row Details */}
                      <AnimatePresence data-id="element-2381">
                        {expandedUserId === user.id && <motion.tr initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} exit={{
                      opacity: 0
                    }} transition={{
                      duration: 0.2
                    }} data-id="element-2382">
                            <td colSpan={7} className="bg-gray-50 px-4 sm:px-6 py-4 border-b border-gray-200" data-id="element-2383">
                              <motion.div initial={{
                          height: 0,
                          opacity: 0
                        }} animate={{
                          height: 'auto',
                          opacity: 1
                        }} exit={{
                          height: 0,
                          opacity: 0
                        }} transition={{
                          duration: 0.3
                        }} className="overflow-hidden" data-id="element-2384">
                                <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2385">
                                  {/* Assigned Pathways */}
                                  <div className="pb-4 border-b border-gray-200" data-id="element-2386">
                                    <p className="text-xs font-medium text-gray-500 mb-3" data-id="element-2387">
                                      Assigned Pathways{' '}
                                      <span className="uppercase" data-id="element-2388">
                                        (
                                        {user.assignedPathways && user.assignedPathways.length > 0 ? `${user.assignedPathways.reduce((sum: number, name: string) => sum + (pathwayData.find(p => p.name === name)?.skillCount || 0), 0)} SKILLS` : '0 SKILLS'}
                                        )
                                      </span>
                                    </p>
                                    {user.assignedPathways && user.assignedPathways.length > 0 ? <div className="flex flex-wrap gap-2" data-id="element-2389">
                                        {user.assignedPathways.map((pathway, idx) => <span key={idx} className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-purple-50 rounded text-xs font-medium text-purple-700" data-id="element-2390">
                                              <span className="text-[10px] font-bold uppercase tracking-wide" data-id="element-2391">
                                                Pathway
                                              </span>
                                              {pathway}
                                            </span>)}
                                      </div> : <p className="text-sm text-gray-400 italic" data-id="element-2392">
                                        No pathways assigned
                                      </p>}
                                  </div>

                                  {/* Assigned Skills */}
                                  <div className="pt-4" data-id="element-2393">
                                    <p className="text-xs font-medium text-gray-500 mb-3" data-id="element-2394">
                                      Assigned Skills{' '}
                                      <span className="uppercase" data-id="element-2395">
                                        (
                                        {user.assignedSkills ? user.assignedSkills.length : 0}{' '}
                                        SKILLS)
                                      </span>
                                    </p>
                                    {user.assignedSkills && user.assignedSkills.length > 0 ? <div className="flex flex-wrap gap-2" data-id="element-2396">
                                        {user.assignedSkills.map((skill, idx) => <span key={idx} className="px-2.5 py-1.5 bg-[#30718D]/10 rounded text-xs font-medium text-[#30718D]" data-id="element-2397">
                                              {skill}
                                            </span>)}
                                      </div> : <p className="text-sm text-gray-400 italic" data-id="element-2398">
                                        No skills assigned
                                      </p>}
                                  </div>
                                </div>
                              </motion.div>
                            </td>
                          </motion.tr>}
                      </AnimatePresence>
                    </Fragment>)}
                </AnimatePresence>
              </tbody>
            </table>

            {/* Pagination */}
            {filteredUsers.length > 0 && totalPages > 1 && <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-t border-gray-200" data-id="element-2399">
                <p className="text-sm text-gray-600" data-id="element-2400">
                  Showing {(safeCurrentPage - 1) * itemsPerPage + 1}–
                  {Math.min(safeCurrentPage * itemsPerPage, filteredUsers.length)}{' '}
                  of {filteredUsers.length}
                </p>
                <div className="flex items-center gap-2" data-id="element-2401">
                  <span className="text-sm text-gray-600" data-id="element-2402">Page</span>
                  <select value={safeCurrentPage} onChange={e => setCurrentPage(Number(e.target.value))} className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent" data-id="element-2403">
                    {Array.from({
                  length: totalPages
                }, (_, i) => i + 1).map(page => <option key={page} value={page} data-id="element-2404">
                        {page}
                      </option>)}
                  </select>
                  <span className="text-sm text-gray-600" data-id="element-2405">of {totalPages}</span>
                </div>
              </div>}
          </div>

          {filteredUsers.length === 0 && <div className="p-12 text-center" data-id="element-2406">
              <Users size={48} className="mx-auto text-gray-400 mb-4" data-id="element-2407" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-2408">
                No users found
              </h3>
              <p className="text-gray-600" data-id="element-2409">
                Try adjusting your search or filter criteria
              </p>
            </div>}
        </div>
      </div>

      {/* Add Individual User Modal */}
      <AnimatePresence data-id="element-2410">
        {showAddModal && <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowAddModal(false)} data-id="element-2411">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-2412">
              <div className="flex items-center justify-between mb-6" data-id="element-2413">
                <div className="flex items-center gap-3" data-id="element-2414">
                  <div className="w-10 h-10 rounded-full bg-[#30718D]/10 flex items-center justify-center" data-id="element-2415">
                    <UserPlus size={20} className="text-[#30718D]" data-id="element-2416" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-2417">
                    {addModalStep === 'form' ? `Add ${userType === 'learners' ? 'Learner' : 'Admin'}` : 'Confirm Registration'}
                  </h2>
                </div>
                <button onClick={() => {
              setShowAddModal(false);
              setIsLicenseDropdownOpen(false);
              setLicenseSearch('');
              setAddModalStep('form');
              setAcknowledgeChecked(false);
              setSelectedModalPathways([]);
              setSelectedLicenses([]);
            }} className="text-sm font-semibold text-gray-700 hover:text-gray-900 underline transition-colors" data-id="element-2418">
                  Close
                </button>
              </div>

              {addModalStep === 'form' ? <>
                  <div className="space-y-4 mb-6" data-id="element-2419">
                    <div data-id="element-2420">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2421">
                        First Name <span className="text-[#DB3725]" data-id="element-2422">*</span>
                      </label>
                      <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent" placeholder="Enter first name" data-id="element-2423" />
                    </div>
                    <div data-id="element-2424">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2425">
                        Last Name <span className="text-[#DB3725]" data-id="element-2426">*</span>
                      </label>
                      <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent" placeholder="Enter last name" data-id="element-2427" />
                    </div>
                    <div data-id="element-2428">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2429">
                        Email Address <span className="text-[#DB3725]" data-id="element-2430">*</span>
                      </label>
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent" placeholder="Enter email address" data-id="element-2431" />
                    </div>
                    <div data-id="element-2432">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2433">
                        Licenses
                      </label>
                      <div className="relative" ref={licenseDropdownRef} data-id="element-2434">
                        <div className={`w-full px-4 py-2 border rounded-lg bg-white flex items-center justify-between cursor-text ${isLicenseDropdownOpen ? 'border-[#30718D] ring-2 ring-[#30718D]' : 'border-gray-300'}`} onClick={() => setIsLicenseDropdownOpen(true)} data-id="element-2435">
                          <input type="text" placeholder={selectedLicenses.length + selectedModalPathways.length > 0 ? `${selectedLicenses.length + selectedModalPathways.length} item${selectedLicenses.length + selectedModalPathways.length > 1 ? 's' : ''} selected — type to search...` : 'Search and select pathways or modules'} value={licenseSearch} onChange={e => {
                      setLicenseSearch(e.target.value);
                      if (!isLicenseDropdownOpen) setIsLicenseDropdownOpen(true);
                    }} onFocus={() => setIsLicenseDropdownOpen(true)} className="flex-1 text-sm bg-transparent outline-none placeholder-gray-400" data-id="element-2436" />
                          <ChevronDown size={16} className={`text-gray-400 transition-transform flex-shrink-0 ml-2 ${isLicenseDropdownOpen ? 'rotate-180' : ''}`} data-id="element-2437" />
                        </div>
                        {isLicenseDropdownOpen && <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-hidden" data-id="element-2438">
                            <div className="max-h-72 overflow-y-auto" data-id="element-2439">
                              {/* Pathways Section */}
                              {pathwayData.filter(p => p.name.toLowerCase().includes(licenseSearch.toLowerCase())).length > 0 && <>
                                  <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100" data-id="element-2440">
                                    <span className="text-xs font-medium text-black uppercase tracking-wide" data-id="element-2441">
                                      Pathways
                                    </span>
                                    <button type="button" onClick={e => {
                            e.stopPropagation();
                            selectAllModalPathways();
                          }} className="text-xs font-medium text-[#30718D] hover:text-[#265a70] transition-colors" data-id="element-2442">
                                      {selectedModalPathways.length === pathwayData.length ? 'Deselect all' : 'Select all'}
                                    </button>
                                  </div>
                                  {pathwayData.filter(p => p.name.toLowerCase().includes(licenseSearch.toLowerCase())).map(pathway => <label key={pathway.name} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100" data-id="element-2443">
                                        <input type="checkbox" checked={selectedModalPathways.includes(pathway.name)} onChange={() => toggleModalPathway(pathway.name)} className="w-4 h-4 rounded border-gray-400 text-[#30718D] focus:ring-[#30718D]" data-id="element-2444" />
                                        <div data-id="element-2445">
                                          <div className="text-sm font-semibold text-gray-900" data-id="element-2446">
                                            {pathway.name}
                                          </div>
                                          <div className="text-xs text-gray-500" data-id="element-2447">
                                            {pathway.skillCount} skills
                                          </div>
                                        </div>
                                      </label>)}
                                </>}

                              {/* Individual Skills Section */}
                              {availableSkills.filter(skill => skill.toLowerCase().includes(licenseSearch.toLowerCase())).length > 0 && <>
                                  <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100" data-id="element-2448">
                                    <span className="text-xs font-medium text-black uppercase tracking-wide" data-id="element-2449">
                                      Individual Skills
                                    </span>
                                    <button type="button" onClick={e => {
                            e.stopPropagation();
                            selectAllModalSkills();
                          }} className="text-xs font-medium text-[#30718D] hover:text-[#265a70] transition-colors" data-id="element-2450">
                                      {selectedLicenses.length === availableSkills.length ? 'Deselect all' : 'Select all'}
                                    </button>
                                  </div>
                                  {availableSkills.filter(skill => skill.toLowerCase().includes(licenseSearch.toLowerCase())).map(skill => <label key={skill} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0" data-id="element-2451">
                                        <input type="checkbox" checked={selectedLicenses.includes(skill)} onChange={() => toggleLicense(skill)} className="w-4 h-4 rounded border-gray-400 text-[#30718D] focus:ring-[#30718D]" data-id="element-2452" />
                                        <span className="text-sm text-gray-700" data-id="element-2453">
                                          {skill}
                                        </span>
                                      </label>)}
                                </>}

                              {pathwayData.filter(p => p.name.toLowerCase().includes(licenseSearch.toLowerCase())).length === 0 && availableSkills.filter(skill => skill.toLowerCase().includes(licenseSearch.toLowerCase())).length === 0 && <p className="px-4 py-3 text-sm text-gray-400" data-id="element-2454">
                                    No results found
                                  </p>}
                            </div>
                          </div>}
                      </div>
                      {(selectedModalPathways.length > 0 || selectedLicenses.length > 0) && <div className="flex flex-wrap gap-1.5 mt-2 max-h-24 overflow-y-auto" data-id="element-2455">
                          {selectedModalPathways.map(pathway => <span key={pathway} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium" data-id="element-2456">
                              <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70 mr-0.5" data-id="element-2457">
                                Pathway
                              </span>
                              {pathway}
                              <button type="button" onClick={() => toggleModalPathway(pathway)} className="hover:text-[#DB3725] transition-colors" data-id="element-2458">
                                ×
                              </button>
                            </span>)}
                          {selectedLicenses.map(skill => <span key={skill} className="inline-flex items-center gap-1 px-2 py-1 bg-[#30718D]/10 text-[#30718D] rounded text-xs font-medium" data-id="element-2459">
                              {skill}
                              <button type="button" onClick={() => toggleLicense(skill)} className="hover:text-[#DB3725] transition-colors" data-id="element-2460">
                                ×
                              </button>
                            </span>)}
                        </div>}
                    </div>
                    {userType === 'admins' && <div data-id="element-2461">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2462">
                          Admin Role
                        </label>
                        <select value={role} onChange={e => setRole(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent" data-id="element-2463">
                          <option value="admin" data-id="element-2464">Admin</option>
                          <option value="super-admin" data-id="element-2465">Super Admin</option>
                        </select>
                      </div>}
                  </div>

                  <div className="flex gap-3" data-id="element-2466">
                    <button onClick={() => {
                setShowAddModal(false);
                setIsLicenseDropdownOpen(false);
                setAddModalStep('form');
                setAcknowledgeChecked(false);
                setSelectedModalPathways([]);
                setSelectedLicenses([]);
              }} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium" data-id="element-2467">
                      Cancel
                    </button>
                    <button onClick={() => {
                setIsLicenseDropdownOpen(false);
                setLicenseSearch('');
                setAddModalStep('confirm');
              }} disabled={!firstName.trim() || !lastName.trim() || !email.trim()} className={`flex-1 px-4 py-2 rounded-lg transition-colors font-medium shadow-sm ${firstName.trim() && lastName.trim() && email.trim() ? 'bg-[#DB3725] text-white hover:bg-[#c02f1f]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-2468">
                      Next
                    </button>
                  </div>
                </> : <>
                  {/* Confirmation Screen */}
                  <p className="text-sm text-gray-600 mb-4" data-id="element-2469">
                    Please ensure all learner information provided is accurate.
                  </p>

                  <div className="flex items-center gap-4 text-xs mb-4" data-id="element-2470">
                    {(() => {
                const totalPathwaySkills = selectedModalPathways.reduce((sum, name) => sum + (pathwayData.find(p => p.name === name)?.skillCount || 0), 0);
                const totalSkills = totalPathwaySkills + selectedLicenses.length;
                return <>
                          <span className="text-gray-500" data-id="element-2471">
                            Licenses to be registered:{' '}
                            <span className="font-bold text-[#30718D]" data-id="element-2472">
                              {totalSkills}
                            </span>
                          </span>
                          <span className="text-gray-300" data-id="element-2473">|</span>
                          <span className="text-gray-500" data-id="element-2474">
                            Remaining:{' '}
                            <span className="font-bold text-gray-900" data-id="element-2475">
                              {190 - totalSkills}
                            </span>
                          </span>
                        </>;
              })()}
                  </div>

                  <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 mb-5" data-id="element-2476">
                    <div className="flex items-center gap-3 mb-3" data-id="element-2477">
                      <div className="w-10 h-10 rounded-full bg-[#30718D] flex items-center justify-center text-white text-sm font-bold" data-id="element-2478">
                        {firstName.charAt(0).toUpperCase()}
                        {lastName.charAt(0).toUpperCase()}
                      </div>
                      <div data-id="element-2479">
                        <p className="text-sm font-semibold text-gray-900" data-id="element-2480">
                          {firstName} {lastName}
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-2481">{email}</p>
                      </div>
                    </div>

                    {selectedModalPathways.length > 0 && <div className="pt-3 border-t border-gray-200" data-id="element-2482">
                        <p className="text-xs font-medium text-gray-500 uppercase mb-2" data-id="element-2483">
                          Assigned Pathways (
                          {selectedModalPathways.reduce((sum, name) => sum + (pathwayData.find(p => p.name === name)?.skillCount || 0), 0)}{' '}
                          skills)
                        </p>
                        <div className="flex flex-wrap gap-1.5" data-id="element-2484">
                          {selectedModalPathways.map(pathway => <span key={pathway} className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium" data-id="element-2485">
                              <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70 mr-0.5" data-id="element-2486">
                                Pathway
                              </span>
                              {pathway}
                            </span>)}
                        </div>
                      </div>}

                    {selectedLicenses.length > 0 && <div className={`pt-3 ${selectedModalPathways.length > 0 ? 'mt-3' : ''} border-t border-gray-200`} data-id="element-2487">
                        <p className="text-xs font-medium text-gray-500 uppercase mb-2" data-id="element-2488">
                          Assigned Skills ({selectedLicenses.length})
                        </p>
                        <div className="flex flex-wrap gap-1.5" data-id="element-2489">
                          {selectedLicenses.map(skill => <span key={skill} className="px-2.5 py-1 bg-[#30718D]/10 text-[#30718D] rounded text-xs font-medium" data-id="element-2490">
                              {skill}
                            </span>)}
                        </div>
                      </div>}
                  </div>

                  <div className="bg-amber-50 rounded-lg p-4 mb-5 border border-amber-100" data-id="element-2491">
                    <p className="text-sm text-gray-700 leading-relaxed" data-id="element-2492">
                      Once you click submit, the learner will receive an email
                      with instructions to create a password and complete their
                      registration.
                    </p>
                  </div>

                  <label className="flex items-start gap-3 mb-6 cursor-pointer" data-id="element-2493">
                    <input type="checkbox" checked={acknowledgeChecked} onChange={e => setAcknowledgeChecked(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-[#30718D] focus:ring-[#30718D] mt-0.5" data-id="element-2494" />
                    <span className="text-sm text-gray-700" data-id="element-2495">
                      I acknowledge that I have the authority to share the
                      information of the learner I provided.
                    </span>
                  </label>

                  <div className="flex gap-3" data-id="element-2496">
                    <button onClick={() => setAddModalStep('form')} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium" data-id="element-2497">
                      Back
                    </button>
                    <button onClick={handleAddUser} disabled={!acknowledgeChecked} className={`flex-1 px-4 py-2 rounded-lg transition-colors font-medium shadow-sm ${acknowledgeChecked ? 'bg-[#DB3725] text-white hover:bg-[#c02f1f]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-2498">
                      Submit
                    </button>
                  </div>
                </>}
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Bulk Upload Modal */}
      <AnimatePresence data-id="element-2499">
        {showBulkModal && <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowBulkModal(false)} data-id="element-2500">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="bg-white rounded-xl p-6 max-w-lg w-full shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-2501">
              <div className="flex items-center justify-between mb-6" data-id="element-2502">
                <div className="flex items-center gap-3" data-id="element-2503">
                  <div className="w-10 h-10 rounded-full bg-[#007558]/10 flex items-center justify-center" data-id="element-2504">
                    <Upload size={20} className="text-[#007558]" data-id="element-2505" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-2506">
                    Bulk Upload{' '}
                    {userType === 'learners' ? 'Learners' : 'Admins'}
                  </h2>
                </div>
                <button onClick={() => setShowBulkModal(false)} className="text-sm font-semibold text-gray-700 hover:text-gray-900 underline transition-colors" data-id="element-2507">
                  Close
                </button>
              </div>

              <div className="mb-6" data-id="element-2508">
                <div onClick={handleSimulateBulkUpload} className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#30718D] transition-colors cursor-pointer" data-id="element-2509">
                  <Upload size={48} className="mx-auto text-gray-400 mb-4" data-id="element-2510" />
                  <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-2511">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-2512">
                    CSV file with columns: First Name, Last Name, Email
                    {userType === 'admins' ? ', Role' : ''}
                  </p>
                </div>
              </div>

              <div className="bg-[#30718D]/5 rounded-lg p-4 mb-6" data-id="element-2513">
                <div className="flex items-start gap-3" data-id="element-2514">
                  <AlertCircle size={20} className="text-[#30718D] mt-0.5" data-id="element-2515" />
                  <div data-id="element-2516">
                    <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-2517">
                      CSV Format Requirements
                    </p>
                    <p className="text-xs text-gray-600" data-id="element-2518">
                      Your CSV must include headers: First Name, Last Name,
                      Email
                      {userType === 'admins' ? ', Role (Admin or Super Admin)' : ''}
                      . Each row represents one{' '}
                      {userType === 'learners' ? 'learner' : 'admin'}.
                    </p>
                  </div>
                </div>
              </div>

              <button className="w-full mb-3 px-4 py-2 border border-[#30718D] bg-white text-[#30718D] rounded-lg hover:bg-[#30718D]/5 transition-colors font-medium flex items-center justify-center gap-2" data-id="element-2519">
                <Download size={16} data-id="element-2520" />
                Download CSV Template
              </button>

              <div className="flex gap-3" data-id="element-2521">
                <button onClick={() => setShowBulkModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium" data-id="element-2522">
                  Cancel
                </button>
                <button className="flex-1 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm" data-id="element-2523">
                  Upload & Import
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Generate Link Modal */}
      <AnimatePresence data-id="element-2524">
        {showLinkModal && <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowLinkModal(false)} data-id="element-2525">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="bg-white rounded-xl p-6 max-w-lg w-full shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-2526">
              <div className="flex items-center justify-between mb-6" data-id="element-2527">
                <div className="flex items-center gap-3" data-id="element-2528">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center" data-id="element-2529">
                    <Link2 size={20} className="text-purple-600" data-id="element-2530" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-2531">
                    {userType === 'learners' ? 'Learner' : 'Admin'} Registration
                    Link Generated
                  </h2>
                </div>
                <button onClick={() => setShowLinkModal(false)} className="text-sm font-semibold text-gray-700 hover:text-gray-900 underline transition-colors" data-id="element-2532">
                  Close
                </button>
              </div>

              <div className="mb-6" data-id="element-2533">
                <p className="text-sm text-gray-600 mb-4" data-id="element-2534">
                  Share this link with{' '}
                  {userType === 'learners' ? 'learners' : 'admins'} to allow
                  them to self-register for the license(s) you select.
                </p>

                {/* Licenses Multi-Select */}
                <div className="mb-4" data-id="element-2535">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2536">
                    Licenses <span className="text-[#DB3725]" data-id="element-2537">*</span>
                  </label>
                  <div className="relative" ref={linkLicenseDropdownRef} data-id="element-2538">
                    <div className={`w-full px-4 py-2 border rounded-lg bg-white flex items-center justify-between cursor-text ${isLinkLicenseDropdownOpen ? 'border-[#30718D] ring-2 ring-[#30718D]' : 'border-gray-300'}`} onClick={() => setIsLinkLicenseDropdownOpen(true)} data-id="element-2539">
                      <input type="text" placeholder={linkLicenses.length + linkPathways.length > 0 ? `${linkLicenses.length + linkPathways.length} item${linkLicenses.length + linkPathways.length > 1 ? 's' : ''} selected — type to search...` : 'Search and select pathways or modules'} value={linkLicenseSearch} onChange={e => {
                    setLinkLicenseSearch(e.target.value);
                    if (!isLinkLicenseDropdownOpen) setIsLinkLicenseDropdownOpen(true);
                  }} onFocus={() => setIsLinkLicenseDropdownOpen(true)} className="flex-1 text-sm bg-transparent outline-none placeholder-gray-400" data-id="element-2540" />
                      <ChevronDown size={16} className={`text-gray-400 transition-transform flex-shrink-0 ml-2 ${isLinkLicenseDropdownOpen ? 'rotate-180' : ''}`} data-id="element-2541" />
                    </div>
                    {isLinkLicenseDropdownOpen && <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-hidden" data-id="element-2542">
                        <div className="max-h-72 overflow-y-auto" data-id="element-2543">
                          {/* Pathways Section */}
                          {pathwayData.filter(p => p.name.toLowerCase().includes(linkLicenseSearch.toLowerCase())).length > 0 && <>
                              <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100" data-id="element-2544">
                                <span className="text-xs font-medium text-black uppercase tracking-wide" data-id="element-2545">
                                  Pathways
                                </span>
                                <button type="button" onClick={e => {
                          e.stopPropagation();
                          selectAllLinkPathways();
                        }} className="text-xs font-medium text-[#30718D] hover:text-[#265a70] transition-colors" data-id="element-2546">
                                  {linkPathways.length === pathwayData.length ? 'Deselect all' : 'Select all'}
                                </button>
                              </div>
                              {pathwayData.filter(p => p.name.toLowerCase().includes(linkLicenseSearch.toLowerCase())).map(pathway => <label key={pathway.name} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100" data-id="element-2547">
                                    <input type="checkbox" checked={linkPathways.includes(pathway.name)} onChange={() => toggleLinkPathway(pathway.name)} className="w-4 h-4 rounded border-gray-400 text-[#30718D] focus:ring-[#30718D]" data-id="element-2548" />
                                    <div data-id="element-2549">
                                      <div className="text-sm font-semibold text-gray-900" data-id="element-2550">
                                        {pathway.name}
                                      </div>
                                      <div className="text-xs text-gray-500" data-id="element-2551">
                                        {pathway.skillCount} skills
                                      </div>
                                    </div>
                                  </label>)}
                            </>}

                          {/* Individual Skills Section */}
                          {availableSkills.filter(skill => skill.toLowerCase().includes(linkLicenseSearch.toLowerCase())).length > 0 && <>
                              <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100" data-id="element-2552">
                                <span className="text-xs font-medium text-black uppercase tracking-wide" data-id="element-2553">
                                  Individual Skills
                                </span>
                                <button type="button" onClick={e => {
                          e.stopPropagation();
                          selectAllLinkSkills();
                        }} className="text-xs font-medium text-[#30718D] hover:text-[#265a70] transition-colors" data-id="element-2554">
                                  {linkLicenses.length === availableSkills.length ? 'Deselect all' : 'Select all'}
                                </button>
                              </div>
                              {availableSkills.filter(skill => skill.toLowerCase().includes(linkLicenseSearch.toLowerCase())).map(skill => <label key={skill} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0" data-id="element-2555">
                                    <input type="checkbox" checked={linkLicenses.includes(skill)} onChange={() => toggleLinkLicense(skill)} className="w-4 h-4 rounded border-gray-400 text-[#30718D] focus:ring-[#30718D]" data-id="element-2556" />
                                    <span className="text-sm text-gray-700" data-id="element-2557">
                                      {skill}
                                    </span>
                                  </label>)}
                            </>}

                          {pathwayData.filter(p => p.name.toLowerCase().includes(linkLicenseSearch.toLowerCase())).length === 0 && availableSkills.filter(skill => skill.toLowerCase().includes(linkLicenseSearch.toLowerCase())).length === 0 && <p className="px-4 py-3 text-sm text-gray-400" data-id="element-2558">
                                No results found
                              </p>}
                        </div>
                      </div>}
                  </div>
                  {(linkPathways.length > 0 || linkLicenses.length > 0) && <div className="flex flex-wrap gap-1.5 mt-2 max-h-24 overflow-y-auto" data-id="element-2559">
                      {linkPathways.map(pathway => <span key={pathway} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium" data-id="element-2560">
                          <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70 mr-0.5" data-id="element-2561">
                            Pathway
                          </span>
                          {pathway}
                          <button type="button" onClick={() => toggleLinkPathway(pathway)} className="hover:text-[#DB3725] transition-colors" data-id="element-2562">
                            ×
                          </button>
                        </span>)}
                      {linkLicenses.map(skill => <span key={skill} className="inline-flex items-center gap-1 px-2 py-1 bg-[#30718D]/10 text-[#30718D] rounded text-xs font-medium" data-id="element-2563">
                          {skill}
                          <button type="button" onClick={() => toggleLinkLicense(skill)} className="hover:text-[#DB3725] transition-colors" data-id="element-2564">
                            ×
                          </button>
                        </span>)}
                    </div>}
                </div>

                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-2565">
                  <div className="flex items-center gap-2 mb-2" data-id="element-2566">
                    <span className="text-xs font-medium text-gray-500 uppercase" data-id="element-2567">
                      Registration Link
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded ${userType === 'learners' ? 'bg-[#30718D]/10 text-[#30718D]' : 'bg-purple-100 text-purple-700'}`} data-id="element-2568">
                      {userType === 'learners' ? 'Learner' : 'Admin'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-2569">
                    <code className="flex-1 text-sm text-gray-900 break-all" data-id="element-2570">
                      {generatedLink}
                    </code>
                    <button onClick={handleCopyLink} className="p-2 text-[#30718D] hover:bg-[#30718D]/10 rounded-lg transition-colors flex-shrink-0" data-id="element-2571">
                      {linkCopied ? <CheckCircle size={18} data-id="element-2572" /> : <Copy size={18} data-id="element-2573" />}
                    </button>
                  </div>
                </div>

                {linkCopied && <motion.p initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="text-sm text-[#007558] mt-2 flex items-center gap-1" data-id="element-2574">
                    <CheckCircle size={14} data-id="element-2575" />
                    Link copied to clipboard!
                  </motion.p>}
              </div>

              <div className="bg-purple-50 rounded-lg p-4 mb-6" data-id="element-2576">
                <div className="flex items-start gap-3" data-id="element-2577">
                  <Clock size={20} className="text-purple-600 mt-0.5" data-id="element-2578" />
                  <div data-id="element-2579">
                    <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-2580">
                      Link Expiration
                    </p>
                    <p className="text-xs text-gray-600" data-id="element-2581">
                      This link will expire in 7 days. You can generate a new
                      link at any time.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3" data-id="element-2582">
                <button onClick={() => {
              setShowLinkModal(false);
              setIsLinkLicenseDropdownOpen(false);
              setLinkLicenseSearch('');
              setLinkPathways([]);
              setLinkLicenses([]);
            }} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium" data-id="element-2583">
                  Close
                </button>
                <button onClick={handleGenerateLink} disabled={linkLicenses.length === 0 && linkPathways.length === 0} className={`flex-1 px-4 py-2 rounded-lg transition-colors font-medium shadow-sm ${linkLicenses.length > 0 || linkPathways.length > 0 ? 'bg-[#DB3725] text-white hover:bg-[#c02f1f]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-2584">
                  Generate New Link
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Floating Action Bar for Bulk Assignment */}
      <AnimatePresence data-id="element-2585">
        {selectedUsers.length > 0 && <motion.div initial={{
        y: 100,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 100,
        opacity: 0
      }} className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.05)] px-4 py-4 sm:px-6 lg:px-8" data-id="element-2586">
            <div className="max-w-[1440px] mx-auto flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4" data-id="element-2587">
              {/* Left side: badge + dropdowns */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1" data-id="element-2588">
                <div className="bg-[#30718D]/10 text-[#30718D] px-3 py-2 rounded-lg font-semibold text-sm whitespace-nowrap text-center sm:text-left" data-id="element-2589">
                  {selectedUsers.length} selected
                </div>

                {/* Assign Skills Dropdown */}
                <div className="relative flex-1 sm:max-w-[240px]" ref={bulkSkillDropdownRef} data-id="element-2590">
                  <button onClick={() => {
                setIsBulkSkillDropdownOpen(!isBulkSkillDropdownOpen);
                setIsBulkPathwayDropdownOpen(false);
              }} className={`w-full px-4 py-2 border rounded-lg bg-white flex items-center justify-between text-sm ${isBulkSkillDropdownOpen ? 'border-[#30718D] ring-2 ring-[#30718D]/20' : 'border-gray-300 hover:border-gray-400'}`} data-id="element-2591">
                    <span className="truncate text-gray-700" data-id="element-2592">
                      {bulkSelectedSkills.length > 0 ? `${bulkSelectedSkills.length} Skills selected` : 'Assign Skills...'}
                    </span>
                    <ChevronDown size={16} className={`text-gray-400 transition-transform flex-shrink-0 ml-2 ${isBulkSkillDropdownOpen ? 'rotate-180' : ''}`} data-id="element-2593" />
                  </button>

                  {isBulkSkillDropdownOpen && <div className="absolute bottom-full mb-2 left-0 w-full sm:w-80 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden z-50" data-id="element-2594">
                      <div className="max-h-80 overflow-y-auto" data-id="element-2595">
                        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100" data-id="element-2596">
                          <span className="text-xs font-medium text-gray-900" data-id="element-2597">
                            Individual Skills
                          </span>
                          <button onClick={() => toggleBulkSkill('all')} className="text-xs font-medium text-[#30718D] hover:text-[#265a70]" data-id="element-2598">
                            {bulkSelectedSkills.length === availableSkills.length ? 'Deselect all' : 'Select all'}
                          </button>
                        </div>
                        {availableSkills.map(skill => <label key={skill} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0" data-id="element-2599">
                            <input type="checkbox" checked={bulkSelectedSkills.includes(skill)} onChange={() => toggleBulkSkill(skill)} className="w-4 h-4 rounded border-gray-300 text-[#30718D] focus:ring-[#30718D]" data-id="element-2600" />
                            <span className="text-sm text-gray-700" data-id="element-2601">
                              {skill}
                            </span>
                          </label>)}
                      </div>
                    </div>}
                </div>

                {/* Assign Pathways Dropdown */}
                <div className="relative flex-1 sm:max-w-[240px]" ref={bulkPathwayDropdownRef} data-id="element-2602">
                  <button onClick={() => {
                setIsBulkPathwayDropdownOpen(!isBulkPathwayDropdownOpen);
                setIsBulkSkillDropdownOpen(false);
              }} className={`w-full px-4 py-2 border rounded-lg bg-white flex items-center justify-between text-sm ${isBulkPathwayDropdownOpen ? 'border-[#30718D] ring-2 ring-[#30718D]/20' : 'border-gray-300 hover:border-gray-400'}`} data-id="element-2603">
                    <span className="truncate text-gray-700" data-id="element-2604">
                      {bulkSelectedPathways.length > 0 ? `${bulkSelectedPathways.length} Pathways selected` : 'Assign Pathways...'}
                    </span>
                    <ChevronDown size={16} className={`text-gray-400 transition-transform flex-shrink-0 ml-2 ${isBulkPathwayDropdownOpen ? 'rotate-180' : ''}`} data-id="element-2605" />
                  </button>

                  {isBulkPathwayDropdownOpen && <div className="absolute bottom-full mb-2 left-0 w-full sm:w-80 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden z-50" data-id="element-2606">
                      <div className="max-h-80 overflow-y-auto" data-id="element-2607">
                        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100" data-id="element-2608">
                          <span className="text-xs font-medium text-gray-900" data-id="element-2609">
                            Pathways
                          </span>
                          <button onClick={() => toggleBulkPathway('all')} className="text-xs font-medium text-[#30718D] hover:text-[#265a70]" data-id="element-2610">
                            {bulkSelectedPathways.length === pathwayData.map(p => p.name).length ? 'Deselect all' : 'Select all'}
                          </button>
                        </div>
                        {pathwayData.map(pathway => <label key={pathway.name} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100" data-id="element-2611">
                            <input type="checkbox" checked={bulkSelectedPathways.includes(pathway.name)} onChange={() => toggleBulkPathway(pathway.name)} className="w-4 h-4 rounded border-gray-300 text-[#30718D] focus:ring-[#30718D]" data-id="element-2612" />
                            <div data-id="element-2613">
                              <div className="text-sm font-bold text-gray-900" data-id="element-2614">
                                {pathway.name}
                              </div>
                              <div className="text-xs text-gray-500" data-id="element-2615">
                                {pathway.skillCount} skills
                              </div>
                            </div>
                          </label>)}
                      </div>
                    </div>}
                </div>
              </div>

              {/* Right side: Cancel + Assign */}
              <div className="flex items-center justify-center lg:justify-end gap-3" data-id="element-2616">
                <button onClick={() => {
              setSelectedUsers([]);
              setBulkSelectedSkills([]);
              setBulkSelectedPathways([]);
            }} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors" data-id="element-2617">
                  Cancel
                </button>
                <button onClick={handleBulkAssign} disabled={bulkSelectedSkills.length === 0 && bulkSelectedPathways.length === 0} className={`flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm ${bulkSelectedSkills.length > 0 || bulkSelectedPathways.length > 0 ? 'bg-[#DB3725] text-white hover:bg-[#c02f1f]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-2618">
                  <CheckCircle size={16} data-id="element-2619" />
                  Assign Selected
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}